/**
 * Streaming CSV / TSV parser (Requirements 10.3, 10.9).
 *
 * ## Why a push interface
 *
 * `push(chunk)` / `flush()` rather than an async iterator: the I/O worker
 * (task 20) drives `File.stream()` with a reader loop, and a push interface lets
 * that loop own the awaiting, the row batching, the progress counter, and the
 * cancel check between chunks. An async-iterator parser would invert that
 * control and make cancellation a matter of aborting a generator mid-`yield`.
 * The parser itself stays synchronous, pure of I/O, and free of DOM and worker
 * globals, so it runs unchanged in Node, in a worker, and on the main thread.
 * {@link parseCsvStream} wraps the same object for callers that do have an async
 * iterable of chunks and want the loop written for them.
 *
 * ## Why an explicit state machine
 *
 * A regex or a `split` cannot parse CSV: a quoted field may contain the
 * delimiter, a newline, or a doubled quote, and — because the parser is fed
 * arbitrary chunks of a byte stream — any of those may straddle a chunk
 * boundary. All partial state (the six machine states, the half-built field, the
 * fields of the half-built record, a pending `\r`, and a half-decoded multi-byte
 * sequence) lives on the instance and survives between `push` calls, which is
 * exactly what makes the parse independent of where the stream was cut
 * (Property 38).
 *
 * ## Encoding
 *
 * The first bytes are sniffed for a byte-order mark: `EF BB BF` selects UTF-8,
 * `FF FE` UTF-16LE, `FE FF` UTF-16BE, and the mark itself is dropped. With no
 * mark, `dialect.encoding` decides. Decoding runs through a streaming
 * `TextDecoder`, so a multi-byte sequence split across chunks is reassembled by
 * the platform rather than by hand. If the host build lacks a decoder for the
 * named encoding (a small-ICU Node build has no `windows-1252`), the parser
 * falls back to UTF-8 and records the substitution on {@link encodingUsed} so
 * the import report can say so; it never throws mid-stream.
 */

import type { CsvDialect } from './dialect.js';
import { resolveDialect } from './dialect.js';

/** One complete record. */
export interface ParsedRow {
  /**
   * 1-based record number in the source, counting the header record and
   * counting a record with embedded newlines once.
   */
  row: number;
  fields: string[];
}

/** A record whose shape does not match the rest of the file (Requirement 10.9). */
export interface BadRow {
  /** 1-based record number, matching {@link ParsedRow.row}. */
  row: number;
  /** Human-readable cause, shown in the import report. */
  reason: string;
  /** Field count seen on this record. */
  fieldCount: number;
  /** Field count established by the header, or by the first record. */
  expectedFieldCount: number;
  /** The fields as parsed, so the report can show what was seen. */
  fields: string[];
}

/** Result of a whole-input parse. */
export interface CsvParseResult {
  rows: ParsedRow[];
  badRows: BadRow[];
}

const QUOTE = 0x22;
const CR = 0x0d;
const LF = 0x0a;

/**
 * Parser states. `afterField` is the position just past a closing quote;
 * `afterCR` is the position just past a bare `\r`, where a following `\n` is
 * part of the same terminator.
 */
type State = 'fieldStart' | 'inField' | 'inQuoted' | 'quoteInQuoted' | 'afterField' | 'afterCR';

/** Longest byte-order mark we look for. */
const MAX_BOM_BYTES = 3;

const EMPTY = new Uint8Array(0);

function concatBytes(a: Uint8Array, b: Uint8Array): Uint8Array {
  if (a.length === 0) return b;
  if (b.length === 0) return a;
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

/** Decoder label plus how many bytes of byte-order mark to drop. */
function sniffBom(bytes: Uint8Array): { label: string | null; skip: number } {
  const [b0, b1, b2] = [bytes[0], bytes[1], bytes[2]];
  if (b0 === 0xef && b1 === 0xbb && b2 === 0xbf) return { label: 'utf-8', skip: 3 };
  if (b0 === 0xff && b1 === 0xfe) return { label: 'utf-16le', skip: 2 };
  if (b0 === 0xfe && b1 === 0xff) return { label: 'utf-16be', skip: 2 };
  // Auto-detect UTF-16LE without BOM (alternating null bytes in ASCII range)
  if (bytes.length >= 8 && bytes[1] === 0x00 && bytes[3] === 0x00 && bytes[5] === 0x00 && bytes[7] === 0x00) {
    return { label: 'utf-16le', skip: 0 };
  }
  return { label: null, skip: 0 };
}

/**
 * Incremental CSV / TSV reader. Feed bytes with {@link push} (or text with
 * {@link pushText}) and finish with {@link flush}.
 */
export class CsvStreamParser {
  readonly dialect: CsvDialect;

  /** Encoding actually used to decode, after byte-order-mark sniffing. */
  private encodingLabel: string;
  private decoder: TextDecoder | null = null;
  private bomResolved = false;
  private bomBuffer: Uint8Array = EMPTY;

  private readonly delimiterCode: number;

  private state: State = 'fieldStart';
  /** Field under construction. */
  private field = '';
  /** Completed fields of the record under construction. */
  private fields: string[] = [];
  /** True once the record under construction has consumed a character. */
  private dirty = false;

  private recordNo = 0;
  private expectedFieldCount: number | null = null;
  private headerFields: string[] | null = null;
  private bad: BadRow[] = [];
  private finished = false;

  constructor(dialect: Partial<CsvDialect> = {}) {
    this.dialect = resolveDialect(dialect);
    this.encodingLabel = this.dialect.encoding;
    this.delimiterCode = this.dialect.delimiter.charCodeAt(0);
  }

  /** Encoding used for decoding, which may differ from the dialect after a BOM. */
  get encodingUsed(): string {
    return this.encodingLabel;
  }

  /** Header fields, once the first record has been read and `hasHeader` is set. */
  get header(): readonly string[] | null {
    return this.headerFields;
  }

  /** Field count the malformed-row check compares against, once established. */
  get fieldCount(): number | null {
    return this.expectedFieldCount;
  }

  /** Malformed records seen so far (Requirement 10.9). Never throws, never pads. */
  get badRows(): readonly BadRow[] {
    return this.bad;
  }

  /** Records completed so far. */
  get rowsEmitted(): number {
    return this.recordNo - this.bad.length;
  }

  /** Feeds one chunk of bytes and returns the records it completed. */
  push(chunk: Uint8Array): ParsedRow[] {
    if (this.finished) throw new Error('CsvStreamParser: push after flush');
    const text = this.decode(chunk, false);
    return text.length === 0 ? [] : this.feed(text);
  }

  /**
   * Feeds already-decoded text. Used by callers that hold a string (a clipboard
   * payload, a test fixture) and by {@link parseCsvText}.
   */
  pushText(text: string): ParsedRow[] {
    if (this.finished) throw new Error('CsvStreamParser: push after flush');
    return this.feed(text);
  }

  /**
   * Ends the stream: emits the final record if the input did not end with a
   * terminator, and returns it together with the complete malformed-row report.
   */
  flush(): CsvParseResult {
    if (this.finished) return { rows: [], badRows: [...this.bad] };
    const tail = this.decode(EMPTY, true);
    const rows = tail.length === 0 ? [] : this.feed(tail);
    this.finished = true;

    if (this.state === 'inQuoted') {
      // A quote was opened and never closed. The record is reported rather than
      // guessed at, and never silently padded (Requirement 10.9).
      this.fields.push(this.field);
      this.recordNo += 1;
      this.bad.push({
        row: this.recordNo,
        reason: 'unterminated quoted field at end of input',
        fieldCount: this.fields.length,
        expectedFieldCount: this.expectedFieldCount ?? this.fields.length,
        fields: this.fields,
      });
      this.resetRecord();
    } else if (this.dirty) {
      const row = this.endRecord();
      if (row) rows.push(row);
    }
    return { rows, badRows: [...this.bad] };
  }

  /* ------------------------------------------------------------- decoding */

  private decode(chunk: Uint8Array, final: boolean): string {
    if (!this.bomResolved) {
      const buffered = concatBytes(this.bomBuffer, chunk);
      if (!final && buffered.length < MAX_BOM_BYTES) {
        // Not enough bytes to tell a mark from data yet. Holding them is what
        // keeps a one-byte-at-a-time stream identical to a single chunk.
        this.bomBuffer = buffered;
        return '';
      }
      this.bomBuffer = EMPTY;
      this.bomResolved = true;
      const { label, skip } = sniffBom(buffered);
      if (label !== null) this.encodingLabel = label;
      this.decoder = this.makeDecoder();
      chunk = skip === 0 ? buffered : buffered.subarray(skip);
    }
    const decoder = this.decoder ?? (this.decoder = this.makeDecoder());
    return decoder.decode(chunk, { stream: !final });
  }

  private makeDecoder(): TextDecoder {
    try {
      return new TextDecoder(this.encodingLabel, { ignoreBOM: true });
    } catch {
      // Small-ICU host without the named decoder: UTF-8 keeps ASCII intact and
      // the substitution is visible through `encodingUsed`.
      this.encodingLabel = 'utf-8';
      return new TextDecoder('utf-8', { ignoreBOM: true });
    }
  }

  /* -------------------------------------------------------- state machine */

  private feed(text: string): ParsedRow[] {
    const out: ParsedRow[] = [];
    const delim = this.delimiterCode;
    const n = text.length;
    let i = 0;

    while (i < n) {
      switch (this.state) {
        case 'afterCR': {
          // `\r\n` is one terminator; the record ended on the `\r`.
          if (text.charCodeAt(i) === LF) i += 1;
          this.state = 'fieldStart';
          break;
        }
        case 'fieldStart': {
          if (text.charCodeAt(i) === QUOTE) {
            i += 1;
            this.dirty = true;
            this.state = 'inQuoted';
          } else {
            this.state = 'inField';
          }
          break;
        }
        case 'inField': {
          let j = i;
          while (j < n) {
            const c = text.charCodeAt(j);
            if (c === delim || c === CR || c === LF) break;
            j += 1;
          }
          if (j > i) {
            this.field += text.slice(i, j);
            this.dirty = true;
            i = j;
          }
          if (i < n) {
            const c = text.charCodeAt(i);
            i += 1;
            if (c === delim) {
              this.endField();
            } else if (c === LF) {
              const row = this.endRecord();
              if (row) out.push(row);
              this.state = 'fieldStart';
            } else {
              const row = this.endRecord();
              if (row) out.push(row);
              this.state = 'afterCR';
            }
          }
          break;
        }
        case 'inQuoted': {
          // Inside quotes the delimiter, `\r`, and `\n` are all data; only a
          // quote can change anything, so scan straight to the next one.
          let j = i;
          while (j < n && text.charCodeAt(j) !== QUOTE) j += 1;
          if (j > i) {
            this.field += text.slice(i, j);
            i = j;
          }
          if (i < n) {
            i += 1;
            this.state = 'quoteInQuoted';
          }
          break;
        }
        case 'quoteInQuoted': {
          if (text.charCodeAt(i) === QUOTE) {
            this.field += '"';
            i += 1;
            this.state = 'inQuoted';
          } else {
            this.state = 'afterField';
          }
          break;
        }
        case 'afterField': {
          const c = text.charCodeAt(i);
          if (c === delim) {
            i += 1;
            this.endField();
          } else if (c === LF) {
            i += 1;
            const row = this.endRecord();
            if (row) out.push(row);
            this.state = 'fieldStart';
          } else if (c === CR) {
            i += 1;
            const row = this.endRecord();
            if (row) out.push(row);
            this.state = 'afterCR';
          } else {
            // Junk after a closing quote (`"a"b`). Kept as data rather than
            // rejected: a lenient reader loses nothing here.
            this.state = 'inField';
          }
          break;
        }
      }
    }
    return out;
  }

  private endField(): void {
    this.fields.push(this.field);
    this.field = '';
    this.dirty = true;
    this.state = 'fieldStart';
  }

  /**
   * Closes the record under construction. Returns the record, or `null` when it
   * was malformed — in which case it is added to the report instead
   * (Requirement 10.9: skip the row, keep going, record the reason).
   */
  private endRecord(): ParsedRow | null {
    this.fields.push(this.field);
    const fields = this.fields;
    this.recordNo += 1;
    const row = this.recordNo;
    this.resetRecord();

    if (this.expectedFieldCount === null) {
      this.expectedFieldCount = fields.length;
      if (this.dialect.hasHeader) this.headerFields = [...fields];
      return { row, fields };
    }
    if (fields.length !== this.expectedFieldCount) {
      this.bad.push({
        row,
        reason: `expected ${this.expectedFieldCount} fields, found ${fields.length}`,
        fieldCount: fields.length,
        expectedFieldCount: this.expectedFieldCount,
        fields: [...fields],
      });
      // Pad short rows with empty strings, trim long rows — do NOT drop them.
      // Real-world CSV files often have variable-width rows (trailing commas,
      // missing values). Excel and Google Sheets import these without data loss.
      if (fields.length < this.expectedFieldCount) {
        while (fields.length < this.expectedFieldCount) fields.push('');
      } else {
        fields.length = this.expectedFieldCount;
      }
      return { row, fields };
    }
    return { row, fields };
  }

  private resetRecord(): void {
    this.fields = [];
    this.field = '';
    this.dirty = false;
  }
}

/** Convenience constructor, for symmetry with the rest of the core modules. */
export function createCsvParser(dialect: Partial<CsvDialect> = {}): CsvStreamParser {
  return new CsvStreamParser(dialect);
}

/** Parses a whole byte buffer in one shot. */
export function parseCsvBytes(
  bytes: Uint8Array,
  dialect: Partial<CsvDialect> = {},
): CsvParseResult {
  const parser = new CsvStreamParser(dialect);
  const rows = parser.push(bytes);
  const tail = parser.flush();
  return { rows: [...rows, ...tail.rows], badRows: tail.badRows };
}

/** Parses a whole string in one shot, skipping the decoding stage. */
export function parseCsvText(text: string, dialect: Partial<CsvDialect> = {}): CsvParseResult {
  const parser = new CsvStreamParser(dialect);
  const rows = parser.pushText(text);
  const tail = parser.flush();
  return { rows: [...rows, ...tail.rows], badRows: tail.badRows };
}

/**
 * Drives the parser from an async iterable of byte chunks — what
 * `File.stream()` yields once wrapped in a reader loop. `onRows` sees each batch
 * as it completes, so the Import_Service can commit batches and report progress
 * without buffering the file.
 */
export async function parseCsvStream(
  chunks: AsyncIterable<Uint8Array>,
  dialect: Partial<CsvDialect> = {},
  onRows?: (rows: ParsedRow[]) => void | Promise<void>,
): Promise<CsvParseResult> {
  const parser = new CsvStreamParser(dialect);
  const collected: ParsedRow[] = [];
  const sink = onRows ?? ((rows: ParsedRow[]) => void collected.push(...rows));
  for await (const chunk of chunks) {
    const rows = parser.push(chunk);
    if (rows.length > 0) await sink(rows);
  }
  const tail = parser.flush();
  if (tail.rows.length > 0) await sink(tail.rows);
  return { rows: collected, badRows: tail.badRows };
}
